<template>
  <section id="analysis-journey" ref="sectionRef" class="analysis-journey">
    <article
      v-for="(step, index) in steps"
      :key="step.key"
      :ref="(el) => setStepRef(el, index)"
      class="journey-step"
      :class="[`is-${step.key}`, { 'is-active': activeIndex === index }]"
      :style="{ '--step-accent': step.accent }"
    >
      <div class="step-pin">
        <img class="step-background" :src="step.image" :alt="step.alt" />
        <div class="step-shade" aria-hidden="true"></div>
      </div>

      <div class="step-content">
        <div class="step-overlay">
          <span class="step-count">{{ String(index + 1).padStart(2, '0') }}/{{ steps.length }}</span>
          <h2>{{ step.title }}</h2>
          <p>{{ step.description }}</p>

          <div v-if="step.badges?.length" class="step-badges" aria-label="Highlights">
            <span v-for="badge in step.badges" :key="badge">{{ badge }}</span>
          </div>

          <v-btn
            v-if="step.showCta"
            color="primary"
            size="large"
            class="step-cta"
            @click="scrollToUpload"
          >
            <v-icon icon="mdi-upload" class="mr-2" />
            {{ t('analysisJourney.cta') }}
          </v-btn>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

const localizedImage = (fileName) => (
  locale.value === 'en'
    ? `/images/landing-story/en/${fileName}`
    : `/images/landing-story/${fileName}`
)

const activeIndex = ref(0)
const sectionRef = ref(null)
const stepRefs = ref([])
let observer = null

const steps = computed(() => [
  {
    key: 'start',
    title: t('analysisJourney.steps.start.title'),
    description: t('analysisJourney.steps.start.description'),
    image: localizedImage('before-floorplan.png'),
    alt: t('analysisJourney.alt.before'),
    accent: '#a56a43',
    badges: [
      t('analysisJourney.visual.before.badgeOne'),
      t('analysisJourney.visual.before.badgeTwo'),
    ],
    showCta: true,
  },
  {
    key: 'analysis',
    title: t('analysisJourney.steps.analysis.title'),
    description: t('analysisJourney.steps.analysis.description'),
    image: localizedImage('tab-analysis.png'),
    alt: t('analysisJourney.alt.tabAnalysis'),
    accent: '#a56a43',
    badges: [
      t('analysisJourney.visual.analysis.strengthOne'),
      t('analysisJourney.visual.analysis.strengthTwo'),
    ],
    showCta: true,
  },
  {
    key: 'improvements',
    title: t('analysisJourney.steps.improvements.title'),
    description: t('analysisJourney.steps.improvements.description'),
    image: localizedImage('tab-improvements.png'),
    alt: t('analysisJourney.alt.tabImprovements'),
    accent: '#a56a43',
    badges: [
      t('analysisJourney.visual.improvements.priority'),
      t('analysisJourney.visual.improvements.title'),
    ],
  },
  {
    key: 'premium',
    title: t('analysisJourney.steps.premium.title'),
    description: t('analysisJourney.steps.premium.description'),
    image: localizedImage('premium-after-floorplan.png'),
    alt: t('analysisJourney.alt.after'),
    accent: '#a56a43',
    badges: [
      t('analysisJourney.visual.premium.before'),
      t('analysisJourney.visual.premium.after'),
    ],
    showCta: true,
  },
])

const setStepRef = (el, index) => {
  if (el) {
    stepRefs.value[index] = el
  }
}

const scrollToUpload = () => {
  document.querySelector('#upload-section')?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  })
}

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    const visible = entries
      .filter((entry) => entry.isIntersecting)
      .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0]

    if (visible) {
      activeIndex.value = Number(visible.target.dataset.stepIndex || 0)
    }
  }, {
    root: null,
    rootMargin: '-42% 0px -42% 0px',
    threshold: [0.18, 0.36, 0.62],
  })

  stepRefs.value.forEach((el, index) => {
    if (el) {
      el.dataset.stepIndex = String(index)
      observer.observe(el)
    }
  })
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<style scoped>
.analysis-journey {
  background: var(--background);
  padding: 0;
}

.journey-step {
  position: relative;
  min-height: 155vh;
  isolation: isolate;
}

.step-pin {
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
}

.step-background,
.step-shade {
  position: absolute;
  inset: 0;
}

.step-background {
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.journey-step.is-start .step-background,
.journey-step.is-premium .step-background {
  object-fit: contain;
  object-position: 72% center;
  padding: clamp(5rem, 8vw, 8rem) clamp(2rem, 8vw, 8rem) clamp(4rem, 7vw, 7rem) 36vw;
  background: #fffaf6;
}

.step-shade {
  z-index: 1;
  background:
    linear-gradient(90deg, rgba(248, 244, 239, 0.95) 0%, rgba(248, 244, 239, 0.72) 33%, rgba(248, 244, 239, 0.09) 64%, rgba(248, 244, 239, 0) 100%),
    linear-gradient(180deg, rgba(43, 31, 24, 0.08), rgba(43, 31, 24, 0.02));
}

.step-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  min-height: 155vh;
  margin-top: -100vh;
  padding: clamp(5rem, 8vw, 8rem) clamp(1rem, 5vw, 6rem);
}

.step-overlay {
  width: min(480px, 100%);
  padding: clamp(1.25rem, 3vw, 2.25rem);
  border: 1px solid rgba(255, 255, 255, 0.62);
  border-radius: 8px;
  color: var(--text-primary);
  background: rgba(255, 252, 248, 0.76);
  box-shadow: 0 24px 76px rgba(65, 46, 34, 0.16);
  backdrop-filter: blur(18px);
}

.step-count {
  display: inline-flex;
  margin-bottom: 1rem;
  color: var(--step-accent);
  font-size: 0.92rem;
  font-weight: var(--font-weight-bold);
}

.step-overlay h2 {
  margin: 0 0 1rem;
  color: var(--text-primary);
  font-size: clamp(2.25rem, 4.4vw, 4.25rem);
  line-height: 0.98;
  letter-spacing: 0;
  text-wrap: balance;
}

.step-overlay p {
  max-width: 440px;
  margin: 0;
  color: #5f5149;
  font-size: clamp(1.05rem, 1.4vw, 1.35rem);
  line-height: 1.5;
}

.step-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1.25rem;
}

.step-badges span {
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  padding: 0.42rem 0.66rem;
  border: 1px solid color-mix(in srgb, var(--step-accent) 24%, transparent);
  border-radius: 8px;
  color: var(--step-accent);
  background: rgba(255, 255, 255, 0.64);
  font-size: 0.86rem;
  font-weight: var(--font-weight-bold);
}

.step-cta {
  margin-top: 1.5rem;
  box-shadow: 0 14px 34px rgba(47, 125, 104, 0.22);
}

@media (max-width: 960px) {
  .journey-step {
    display: grid;
    min-height: 0;
    padding: 0;
    background: #fffaf6;
  }

  .journey-step + .journey-step {
    border-top: 1px solid rgba(167, 111, 74, 0.14);
  }

  .step-background {
    position: relative;
    z-index: 0;
    inset: auto;
    height: clamp(260px, 66vw, 420px);
    object-fit: cover;
    object-position: top center;
  }

  .journey-step.is-start .step-background,
  .journey-step.is-premium .step-background {
    height: clamp(260px, 76vw, 420px);
    padding: 2rem;
    object-fit: contain;
    object-position: center;
  }

  .step-shade {
    display: none;
  }

  .step-pin {
    position: relative;
    height: auto;
  }

  .step-content {
    display: block;
    min-height: 0;
    margin-top: 0;
    padding: 0;
  }

  .step-overlay {
    width: auto;
    margin: -2.5rem 1rem 1.25rem;
    padding: 1.15rem;
    background: rgba(255, 252, 248, 0.9);
  }

  .step-count {
    margin-bottom: 0.65rem;
  }

  .step-overlay h2 {
    font-size: clamp(1.85rem, 10vw, 3rem);
    line-height: 1.04;
  }

  .step-overlay p {
    font-size: 1rem;
  }
}

@media (max-width: 600px) {
  .step-background {
    height: 250px;
  }

  .journey-step.is-start .step-background,
  .journey-step.is-premium .step-background {
    height: 260px;
    padding: 1.25rem;
  }

  .step-overlay {
    margin-inline: 0.75rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-pin {
    position: relative;
  }
}
</style>
